import { Fragment } from "react";

/**
 * Minimal Markdown → React renderer for assistant replies.
 * Builds elements directly (no innerHTML), so model output can't inject HTML.
 * Supports: headings, paragraphs, ordered / bullet lists, fenced code,
 * **bold**, *italic* / _italic_, `code` and [links](https://…).
 */

const INLINE_RE = /(\*\*([^*]+)\*\*|__([^_]+)__|`([^`]+)`|\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|(?<![\w*])\*([^*\s][^*]*)\*(?![\w*])|(?<!\w)_([^_\s][^_]*)_(?!\w))/g;

export function looksLikeMarkdown(text) {
  return /(\*\*[^*]+\*\*|^\s*#{1,6}\s|^\s*(\d+[.)]|[-*•])\s+|`[^`]+`|\[[^\]]+\]\(https?:)/m.test(String(text || ""));
}

function renderInline(input, keyPrefix) {
  const text = String(input ?? "");
  const out = [];
  let last = 0;
  let i = 0;
  text.replace(INLINE_RE, (match, _all, bold, bold2, code, linkText, linkHref, em, em2, offset) => {
    if (offset > last) out.push(text.slice(last, offset));
    const key = `${keyPrefix}-${i++}`;
    if (bold || bold2) out.push(<strong key={key}>{renderInline(bold || bold2, key)}</strong>);
    else if (code) out.push(<code key={key} style={{ display: "inline", padding: "1px 5px", borderRadius: 5, background: "rgba(15,23,42,0.06)" }}>{code}</code>);
    else if (linkText) out.push(<a key={key} href={linkHref} target="_blank" rel="noopener noreferrer">{linkText}</a>);
    else out.push(<em key={key}>{renderInline(em || em2, key)}</em>);
    last = offset + match.length;
    return match;
  });
  if (last < text.length) out.push(text.slice(last));
  return out;
}

/** Split a reply into blocks: code fences, headings, lists and paragraphs. */
function parseBlocks(src) {
  const lines = String(src || "").replace(/\r\n?/g, "\n").split("\n");
  const blocks = [];
  let para = [];
  const flushPara = () => {
    if (para.length) blocks.push({ type: "p", lines: para });
    para = [];
  };

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];

    if (/^\s*```/.test(line)) {
      flushPara();
      const code = [];
      for (i += 1; i < lines.length && !/^\s*```/.test(lines[i]); i += 1) code.push(lines[i]);
      blocks.push({ type: "code", text: code.join("\n") });
      continue;
    }

    const heading = line.match(/^\s*(#{1,6})\s+(.*)$/);
    if (heading) {
      flushPara();
      blocks.push({ type: "h", level: Math.min(heading[1].length, 3), text: heading[2] });
      continue;
    }

    const listMatch = line.match(/^(\s*)(\d+[.)]|[-*•])\s+(.*)$/);
    if (listMatch) {
      flushPara();
      const ordered = /\d/.test(listMatch[2]);
      const items = [];
      while (i < lines.length) {
        const m = lines[i].match(/^(\s*)(\d+[.)]|[-*•])\s+(.*)$/);
        if (m && /\d/.test(m[2]) === ordered) {
          items.push({ text: m[3], start: ordered ? parseInt(m[2], 10) : null });
          i += 1;
        } else if (lines[i].trim() && /^\s{2,}/.test(lines[i]) && items.length) {
          // Indented continuation line belongs to the previous item.
          items[items.length - 1].text += ` ${lines[i].trim()}`;
          i += 1;
        } else break;
      }
      i -= 1;
      blocks.push({ type: ordered ? "ol" : "ul", items });
      continue;
    }

    if (!line.trim()) flushPara();
    else para.push(line);
  }
  flushPara();
  return blocks;
}

export function renderMarkdown(src) {
  return parseBlocks(src).map((block, b) => {
    const key = `b${b}`;
    switch (block.type) {
      case "code":
        return <pre key={key}><code>{block.text}</code></pre>;
      case "h": {
        const Tag = `h${block.level + 1}`;
        return <Tag key={key}>{renderInline(block.text, key)}</Tag>;
      }
      case "ol":
        return (
          <ol key={key} start={block.items[0]?.start || 1}>
            {block.items.map((item, n) => <li key={`${key}-${n}`}>{renderInline(item.text, `${key}-${n}`)}</li>)}
          </ol>
        );
      case "ul":
        return (
          <ul key={key}>
            {block.items.map((item, n) => <li key={`${key}-${n}`}>{renderInline(item.text, `${key}-${n}`)}</li>)}
          </ul>
        );
      default:
        return (
          <p key={key}>
            {block.lines.map((l, n) => (
              <Fragment key={`${key}-${n}`}>
                {n ? <br /> : null}
                {renderInline(l, `${key}-${n}`)}
              </Fragment>
            ))}
          </p>
        );
    }
  });
}
